import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';

interface Internship {
  _id?: string;
  company?: string;
  role?: string;
  startDate?: string;
  endDate?: string;
  stipendInr?: number;
  description?: string;
  certificateUrl?: string;
}

/**
 * /internships — student internship history + a shortcut to browse open internships.
 *
 * Deep-audit fixes (Page 20/N):
 *   - Empty-state hint pointed the student to 'Documents → certificate'
 *     as the upload path, but /documents currently shows the Add-document
 *     button as disabled ('soon'). Now points them at the placement
 *     office as the working path.
 *   - Rows with a startDate but no endDate rendered the range as a
 *     trailing en-dash ('Jun 2025 – ') which looked like a formatting
 *     bug. Now says 'Jun 2025 – ongoing' when endDate is absent.
 */
export function StudentInternshipsPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'internships'],
    queryFn: () => apiGet<{ rows: Internship[] }>('/students/me/internships'),
  });

  const rows = query.data?.rows ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Internships</h1>
          <p className="subhead">Internships you have done. Recruiters read them before offers.</p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/explore?type=internship">Browse open internships</Link>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
        emptyTitle="No internships recorded yet"
        emptyHint="Ask your placement office to add and verify one — it typically comes from your resume upload plus a certificate you email over."
      >
        <div className="grid gap-3">
          {rows.map((i, idx) => (
            <div key={i._id ?? idx} className="card card-pad grid gap-2">
              <div className="cell-title">{i.role ?? 'Intern'} · {i.company ?? '—'}</div>
              <div className="cell-sub">
                {i.startDate ? new Date(i.startDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                {i.startDate
                  ? ` – ${i.endDate ? new Date(i.endDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'ongoing'}`
                  : ''}
                {i.stipendInr ? ` · ₹${i.stipendInr.toLocaleString('en-IN')}/mo stipend` : ''}
              </div>
              {i.description && <p className="text-sm m-0">{i.description}</p>}
              {i.certificateUrl && <a className="link text-sm" href={i.certificateUrl} target="_blank" rel="noopener noreferrer">View certificate</a>}
            </div>
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
}
